import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import InputMask from 'react-input-mask'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import CustomInput from '../../../../components/muiComponents/customInput'
import { H6 } from '../../../../layouts/TextStyles'
import { studentApiSlice } from '../../../../features/student/studentSlice'
import { requiredMobile, requiredString } from '../../../../utils/validation'
import { convertToMobile } from '../../../../utils/functions'
import { StudentState } from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    editStudent: StudentState
}

const EditStudentDialog: React.FC<Props> = ({ editStudent }) => {
    const { formatMessage } = useIntl()
    const [openDialog, setOpenDialog] = useState(false)
    const [updateStudent, { isLoading: isUpdating }] =
        studentApiSlice.useUpdateStudentMutation()
    const student: StudentState = {
        id: editStudent?.id,
        firstName: editStudent?.firstName,
        lastName: editStudent?.lastName,
        mobile: editStudent?.mobile,
        address: editStudent?.address || ""
    }

    const validationSchema = yup.object().shape({
        firstName: requiredString({ formatMessage }),
        lastName: requiredString({ formatMessage }),
        mobile: requiredMobile({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: student,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            handleUpdateStudent()
        },
    })

    const handleUpdateStudent = async () => {
        try {
            await updateStudent({
                ...formik.values,
                mobile: convertToMobile(formik.values.mobile),
            }).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleCloseDialog()
        } catch (error) {}
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
    }
    const handleClickOpen = () => {
        setOpenDialog(true)
        formik.setValues(student)
    }

    return (
        <div>
            <IconButton
                sx={{ background: ui.track }}
                onClick={handleClickOpen}
            >
                <img
                    src={EditIcon}
                    alt="edit"
                />
            </IconButton>

            <Dialog
                maxWidth={'sm'}
                fullWidth
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'editData' })}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'name' })}</H6>

                            <CustomInput
                                id="firstName"
                                name="firstName"
                                placeholder={formatMessage({
                                    id: 'firstName',
                                })}
                                fullWidth
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                helperText={formik.errors.firstName}
                                error={Boolean(formik.errors.firstName)}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'lastName' })}</H6>

                            <CustomInput
                                id="lastName"
                                name="lastName"
                                placeholder={formatMessage({
                                    id: 'lastName',
                                })}
                                fullWidth
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                helperText={formik.errors.lastName}
                                error={Boolean(formik.errors.lastName)}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>Адрес</H6>

                            <CustomInput
                                id="address"
                                name="address"
                                placeholder="Адрес"
                                fullWidth
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'mobile' })}</H6>
                            <InputMask
                                mask="+7 (999) 999-99-99"
                                value={formik.values.mobile}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            >
                                <CustomInput
                                    id="mobile"
                                    name="mobile"
                                    placeholder={formatMessage({
                                        id: 'enterPhone',
                                    })}
                                    fullWidth
                                    helperText={formik.errors.mobile}
                                    error={Boolean(formik.errors.mobile)}
                                    margin="dense"
                                />
                            </InputMask>
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        onClick={() => formik.handleSubmit()}
                        disabled={isUpdating}
                    >
                        {isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default EditStudentDialog
